import { registerPlugin } from "@capacitor/core";
import { currentPlatform, isNativeShell } from "./shell";

/**
 * Haptics behind the `Platform.supportsHaptics` flag (FINISH_PLAN WP4.3, S5). The iOS shell uses Capacitor Haptics
 * (the Taptic Engine); the browser uses the Vibration API where it exists (Android) and, on iOS Safari 18, the one
 * haptic the web gets: toggling an `<input type=checkbox switch>` ticks the Taptic Engine, so a hidden switch is
 * toggled on each call. Callers name what happened, not how it feels.
 */
export type HapticKind = "tap" | "success" | "warning" | "heavy";

/** Vibration patterns (ms) for the browsers that vibrate: short enough to read as a tick, not a buzz. */
const VibrationMs: Record<HapticKind, number | number[]> = { tap: 8, success: [10, 40, 16], warning: [24, 60, 24], heavy: 30 };

interface HapticsPlugin {
  impact(o: { style: "LIGHT" | "MEDIUM" | "HEAVY" }): Promise<void>;
  notification(o: { type: "SUCCESS" | "WARNING" | "ERROR" }): Promise<void>;
}
const NativeHaptics = registerPlugin<HapticsPlugin>("Haptics");

export class Haptics {
  /** The player's switch (WP10 wires a setting to it); on by default where haptics exist. */
  enabled = true;
  private switchLabel: HTMLLabelElement | null = null;

  constructor(private readonly native: boolean = isNativeShell(), readonly available: boolean = currentPlatform().supportsHaptics) {}

  play(kind: HapticKind): void {
    if (!this.enabled || !this.available) return;
    try {
      if (this.native) { void this.playNative(kind).catch(() => undefined); return; }
      if (typeof navigator.vibrate === "function") { navigator.vibrate(VibrationMs[kind]); return; }
      this.tickSwitch();
    } catch { /* haptics are a nicety: never an error */ }
  }

  private playNative(kind: HapticKind): Promise<void> {
    switch (kind) {
      case "tap": return NativeHaptics.impact({ style: "LIGHT" });
      case "heavy": return NativeHaptics.impact({ style: "HEAVY" });
      case "success": return NativeHaptics.notification({ type: "SUCCESS" });
      case "warning": return NativeHaptics.notification({ type: "WARNING" });
    }
  }

  /**
   * iOS Safari 18: a click on a switch's label toggles it and ticks; the switch is invisible and outside the layout.
   * WebKit ticks only for a toggle inside a user activation (a tap), so outside one (a leak, a win between taps) the
   * click is skipped rather than flipping a switch for nothing.
   */
  private tickSwitch(): void {
    const activation = (navigator as { userActivation?: { isActive: boolean } }).userActivation;
    if (activation && !activation.isActive) return;
    if (!this.switchLabel) {
      const label = document.createElement("label");
      label.setAttribute("aria-hidden", "true");
      label.style.cssText = "position:fixed;left:-100px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.setAttribute("switch", "");
      input.tabIndex = -1;
      label.append(input);
      document.body.append(label);
      this.switchLabel = label;
    }
    this.switchLabel.click();
  }
}

/** The game's haptics. */
export const haptics = new Haptics();
